import { logger } from '../../../utils/logger'
import { getJsonSessionItem, SessionStorage } from '../../../utils/session-storage'
import type { ServiceHandler } from './service-registry'
import { getSessionStorageCustomQuestionsKey } from './setCustomQuestionAnswer'

/**
 * GET_CUSTOM_QUESTION_ANSWER service handler
 *
 * Handles requests to retrieve the answer for a single Custom Question.
 * Retrieves the answer from session storage under the key `custom_questions_{campaignId}`.
 *
 * Expected payload: number (questionId)
 * Expected response format:
 * - Success: "answer" (string)
 * - Error: "error message" if questionId does not exist
 */
export const getCustomQuestionAnswer: ServiceHandler = async ({
  iframeId,
  messageId,
  payload,
  sendSuccessResponse,
  sendErrorResponse,
  pageConfig,
}) => {
  const campaignId = pageConfig.campaignId

  try {
    if (!campaignId) {
      throw new Error('CustomEmbeddedCode:getCustomQuestionAnswer: Campaign ID not available')
    }

    // TODO: CL-51093 improve payload validation with Yup/Zod library
    if (!payload || (typeof payload !== 'number' && typeof payload !== 'string')) {
      sendErrorResponse(iframeId, messageId, 'Question ID is required and must be a number')
      return
    }

    const questionId = payload
    const storageKey = getSessionStorageCustomQuestionsKey(campaignId)
    const customQuestionAnswers = getJsonSessionItem<SessionStorage>(storageKey) || {}

    // Fallback to string if question not found
    const answer = customQuestionAnswers[questionId] || ''

    logger(
      'info',
      'CustomEmbeddedCode:getCustomQuestionAnswer: Successfully retrieved Custom Question answer',
      {
        context: { iframeId, messageId, campaignId, questionId, storageKey },
      },
    )

    sendSuccessResponse(iframeId, messageId, answer)
  } catch (error) {
    logger(
      'error',
      new Error(
        'CustomEmbeddedCode:getCustomQuestionAnswer: Failed to get Custom Question answer',
        {
          cause: error,
        },
      ),
      {
        context: { iframeId, messageId, campaignId },
      },
    )
    sendErrorResponse(iframeId, messageId, 'Unable to get Custom Question answer')
  }
}
